import { Injectable } from '@angular/core';
import { WebSocketService } from '../websocket/websocket.service';

/** Polling when Socket.IO is disconnected (GM / dashboard roles). */
export const DASHBOARD_POLLING_WS_DISCONNECTED_MS = 60_000;

/** Safety poll when connected but no socket-driven refresh recently. */
export const DASHBOARD_POLLING_WS_CONNECTED_SAFETY_MS = 300_000;

/** Debounce coalesced socket refresh bursts. */
export const DASHBOARD_SOCKET_DEBOUNCE_MS = 300;

/** AM / legacy GM overview components (HTTP-only). */
export const DASHBOARD_HTTP_ONLY_REFRESH_MS = 30_000;

export type DashboardSocketHandler = {
  event: string;
  fn: (...args: unknown[]) => void;
};

@Injectable({
  providedIn: 'root',
})
export class DashboardLiveSyncService {
  lastSocketRefresh: Date | null = null;

  /** Normalize tenant id from string, ObjectId, or populated tenant document. */
  normalizeTenantId(raw: unknown): string | undefined {
    if (raw == null || raw === '') {
      return undefined;
    }
    if (typeof raw === 'object' && raw !== null && '_id' in (raw as object)) {
      return String((raw as { _id: unknown })._id);
    }
    return String(raw);
  }

  /**
   * Active dashboard tenant: GM uses assigned shop (`currentTenant`); others use user tenant.
   */
  resolveDashboardTenantId(user: { roles?: string[]; tenant?: unknown; tenantId?: unknown } | null | undefined, gmCurrentTenant?: unknown): string | undefined {
    if (!user) {
      return undefined;
    }
    if (user.roles?.includes('gm') && gmCurrentTenant != null && gmCurrentTenant !== '') {
      return this.normalizeTenantId(gmCurrentTenant);
    }
    return this.normalizeTenantId(user.tenantId ?? user.tenant);
  }

  /**
   * Server `taskUpdate` uses nested `data.metadata.tenant`; client emits use top-level `tenantId`.
   * `updateViewTrigger` from role routing may omit tenant when already scoped to the socket.
   */
  socketPayloadAppliesToThisTenant(
    payload: unknown,
    myTenant: string | undefined,
    viewerRoleNames: string[] = []
  ): boolean {
    if (myTenant == null || myTenant === '') {
      return false;
    }
    const p = payload as Record<string, unknown>;

    if (p['tenantBroadcast'] === true) {
      return false;
    }

    const targetRole = p['roleName'];
    if (p['roleSpecific'] === true && targetRole != null && targetRole !== '') {
      const normalizedTarget = String(targetRole).toLowerCase();
      const roles = viewerRoleNames.map((r) => String(r).toLowerCase());
      if (roles.length > 0 && !roles.includes(normalizedTarget)) {
        return false;
      }
    }

    const topTenant = p['tenantId'];
    if (topTenant != null && topTenant !== '') {
      return String(topTenant) === String(myTenant);
    }
    if (p['workflow'] === 'exec' || p['workflow'] === 'tech') {
      const meta = p['data'] as Record<string, unknown> | undefined;
      const metadata = meta?.['metadata'] as Record<string, unknown> | undefined;
      const metaTenant = metadata?.['tenant'];
      if (metaTenant != null && metaTenant !== '') {
        const tid =
          typeof metaTenant === 'object' && metaTenant !== null && '_id' in (metaTenant as object)
            ? (metaTenant as { _id: unknown })._id
            : metaTenant;
        return String(tid) === String(myTenant);
      }
    }
    const meta = p['data'] as Record<string, unknown> | undefined;
    const metadata = meta?.['metadata'] as Record<string, unknown> | undefined;
    const metaTenant = metadata?.['tenant'];
    if (metaTenant != null && metaTenant !== '') {
      const tid =
        typeof metaTenant === 'object' && metaTenant !== null && '_id' in (metaTenant as object)
          ? (metaTenant as { _id: unknown })._id
          : metaTenant;
      return String(tid) === String(myTenant);
    }
    if (p['type'] === 'newTask' && p['requestId']) {
      if (myTenant == null || myTenant === '') {
        return true;
      }
      const topTenant = p['tenantId'];
      if (topTenant != null && topTenant !== '') {
        return String(topTenant) === String(myTenant);
      }
      return true;
    }
    return false;
  }

  getPollingIntervalMs(isWebSocketConnected: boolean): number {
    return isWebSocketConnected
      ? DASHBOARD_POLLING_WS_CONNECTED_SAFETY_MS
      : DASHBOARD_POLLING_WS_DISCONNECTED_MS;
  }

  shouldRunSafetyPoll(isWebSocketConnected: boolean, lastSocketRefresh: Date | null): boolean {
    if (!isWebSocketConnected) {
      return true;
    }
    if (!lastSocketRefresh) {
      return true;
    }
    return Date.now() - lastSocketRefresh.getTime() > DASHBOARD_POLLING_WS_CONNECTED_SAFETY_MS;
  }

  markSocketRefresh(): void {
    this.lastSocketRefresh = new Date();
  }

  /**
   * Register task refresh socket listeners once (CSR / dashboard pattern).
   */
  bindTaskRefreshSocket(
    ws: WebSocketService,
    handlersRef: DashboardSocketHandler[],
    bindAttemptsRef: { count: number },
    tenantIdResolver: () => string | undefined,
    onRefresh: (payload: unknown) => void,
    maxAttempts = 15,
    viewerRolesResolver?: () => string[]
  ): void {
    if (handlersRef.length > 0) {
      return;
    }
    const io = ws.getIo();
    if (!io) {
      ws.connect();
      bindAttemptsRef.count += 1;
      if (bindAttemptsRef.count < maxAttempts) {
        setTimeout(
          () =>
            this.bindTaskRefreshSocket(
              ws,
              handlersRef,
              bindAttemptsRef,
              tenantIdResolver,
              onRefresh,
              maxAttempts,
              viewerRolesResolver
            ),
          2000
        );
      }
      return;
    }

    const onPayload = (payload: unknown) => {
      const viewerRoles = viewerRolesResolver?.() ?? [];
      if (this.socketPayloadAppliesToThisTenant(payload, tenantIdResolver(), viewerRoles)) {
        this.markSocketRefresh();
        onRefresh(payload);
      }
    };

    const events = ['taskUpdate', 'sendUpdateMessageToRole', 'updateViewTrigger'] as const;
    for (const event of events) {
      io.on(event, onPayload);
      handlersRef.push({ event, fn: onPayload });
    }
  }

  unbindTaskRefreshSocket(ws: WebSocketService, handlersRef: DashboardSocketHandler[]): void {
    const io = ws.getIo();
    if (io) {
      for (const { event, fn } of handlersRef) {
        io.off(event, fn);
      }
    }
    handlersRef.length = 0;
  }
}
